import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { analyticsApi, measurementsApi } from '@/lib/api';
import { formatWork, formatDate } from '@/lib/utils';
import type { CalendarData, PersonalRecord } from '@/types';
import { WeightIcon, MuscleIcon, RepeatIcon, ChartIcon, TargetIcon, FlameIcon, TrophyIcon } from '@/components/AchievementIcons';
import LoadingSpinner from '@/components/LoadingSpinner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

type TabMode = 'training' | 'body';

const MEASUREMENT_FIELDS = [
  { key: 'weight', label: 'Вес', unit: 'кг', color: '#3b82f6' },
  { key: 'chest', label: 'Грудь', unit: 'см', color: '#ef4444' },
  { key: 'waist', label: 'Талия', unit: 'см', color: '#f97316' },
  { key: 'hips', label: 'Бёдра', unit: 'см', color: '#eab308' },
  { key: 'biceps_left', label: 'Бицепс Л', unit: 'см', color: '#22c55e' },
  { key: 'biceps_right', label: 'Бицепс П', unit: 'см', color: '#14b8a6' },
  { key: 'thigh_left', label: 'Бедро Л', unit: 'см', color: '#8b5cf6' },
  { key: 'thigh_right', label: 'Бедро П', unit: 'см', color: '#ec4899' },
] as const;

type MeasurementKey = (typeof MEASUREMENT_FIELDS)[number]['key'];

// Format date as YYYY-MM-DD in local timezone
function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Compact GitHub-style heatmap (current week on the right)
function CalendarHeatmap({ days }: { days: CalendarData['days'] }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Create a map of dates to workout data
  const dayMap = new Map(days.map(d => [d.date, d]));
  
  // Find Monday of current week
  const currentWeekMonday = new Date(today);
  const dayOfWeek = currentWeekMonday.getDay();
  currentWeekMonday.setDate(currentWeekMonday.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  
  // Generate exactly 26 weeks (half year) ending with current week
  const totalWeeks = 26;
  const weeks: { date: Date; data: CalendarData['days'][0] | null }[][] = [];
  
  // Start from 25 weeks before current week's Monday
  const startMonday = new Date(currentWeekMonday);
  startMonday.setDate(startMonday.getDate() - (totalWeeks - 1) * 7);
  
  // Track months for labels
  const monthLabels: { month: number; startWeek: number }[] = [];
  let lastMonth = -1;
  
  const cursor = new Date(startMonday);
  
  for (let w = 0; w < totalWeeks; w++) {
    const week: { date: Date; data: CalendarData['days'][0] | null }[] = [];
    
    for (let d = 0; d < 7; d++) {
      const dateStr = formatLocalDate(cursor);
      const dayData = dayMap.get(dateStr) || null;
      week.push({ date: new Date(cursor), data: dayData });
      cursor.setDate(cursor.getDate() + 1);
    }
    
    // Track month changes (use Thursday of the week)
    const midWeek = week[3]?.date;
    if (midWeek && midWeek.getMonth() !== lastMonth) {
      monthLabels.push({ month: midWeek.getMonth(), startWeek: w });
      lastMonth = midWeek.getMonth();
    }
    
    weeks.push(week);
  }

  const cellSize = 12;
  const gap = 2;

  return (
    <div>
      {/* Month labels */}
      <div className="flex mb-1" style={{ marginLeft: 0 }}>
        {monthLabels.map((m, i) => {
          const nextStart = monthLabels[i + 1]?.startWeek ?? totalWeeks;
          const width = (nextStart - m.startWeek) * (cellSize + gap);
          return (
            <div 
              key={i} 
              style={{ width: `${width}px`, minWidth: `${width}px` }} 
              className="text-left text-[10px] text-gray-400"
            >
              {String(m.month + 1).padStart(2, '0')}
            </div>
          );
        })}
      </div>
      
      {/* Grid */}
      <div className="flex gap-[2px]">
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-[2px]">
            {week.map((day, di) => {
              const isToday = day.date.toDateString() === today.toDateString();
              const isFuture = day.date > today;
              const hasWorkout = day.data && day.data.workouts_count > 0;
              
              return (
                <div
                  key={di}
                  style={{ width: cellSize, height: cellSize }}
                  className={`rounded-sm ${
                    isFuture 
                      ? 'bg-gray-50' 
                      : hasWorkout 
                        ? 'bg-green-500' 
                        : 'bg-gray-100'
                  } ${isToday ? 'ring-1 ring-blue-500' : ''}`}
                  title={
                    hasWorkout
                      ? `${day.date.toLocaleDateString('ru')}: ${day.data!.total_sets} подх.`
                      : day.date.toLocaleDateString('ru')
                  }
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}


// Achievement card for Top-5
function AchievementCard({ 
  icon: IconComponent, 
  title, 
  value, 
  subtitle, 
  color 
}: { 
  icon: React.ComponentType<{ className?: string }>; 
  title: string; 
  value: string; 
  subtitle: string; 
  color: string;
}) {
  const colorClasses: Record<string, string> = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-500 to-green-600',
    orange: 'from-orange-500 to-orange-600',
    pink: 'from-pink-500 to-pink-600',
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-xl p-4 text-white`}>
      <div className="flex items-start justify-between">
        <IconComponent className="w-7 h-7 opacity-90" />
        <div className="text-right">
          <div className="text-xl font-bold">{value}</div>
          <div className="text-xs opacity-80">{title}</div>
        </div>
      </div>
      <div className="mt-2 text-sm opacity-90 truncate">{subtitle}</div>
    </div>
  );
}

// Calculate top achievements from records
function getTopAchievements(records: PersonalRecord[]) {
  if (records.length === 0) return [];

  const achievements: { icon: React.ComponentType<{ className?: string }>; title: string; value: string; subtitle: string; color: string }[] = [];

  // 1. Heaviest weight
  const heaviest = records.reduce((max, r) => r.max_weight > max.max_weight ? r : max, records[0]);
  if (heaviest.max_weight > 0) {
    achievements.push({
      icon: WeightIcon,
      title: 'Макс. вес',
      value: `${heaviest.max_weight} кг`,
      subtitle: heaviest.exercise_name,
      color: 'blue',
    });
  }

  // 2. Best single set (most work)
  const bestSet = records.reduce((max, r) => r.max_work_set > max.max_work_set ? r : max, records[0]);
  if (bestSet.max_work_set > 0) {
    achievements.push({
      icon: MuscleIcon,
      title: 'Лучший подход',
      value: formatWork(bestSet.max_work_set),
      subtitle: bestSet.exercise_name,
      color: 'purple',
    });
  }

  // 3. Most trained exercise (by sets)
  const mostTrained = records.reduce((max, r) => r.total_sets_all_time > max.total_sets_all_time ? r : max, records[0]);
  if (mostTrained.total_sets_all_time > 0) {
    achievements.push({
      icon: RepeatIcon,
      title: 'Частое',
      value: `${mostTrained.total_sets_all_time} подх.`,
      subtitle: mostTrained.exercise_name,
      color: 'green',
    });
  }

  // 4. Most volume exercise (total work)
  const mostVolume = records.reduce((max, r) => r.total_work_all_time > max.total_work_all_time ? r : max, records[0]);
  if (mostVolume.total_work_all_time > 0) {
    achievements.push({
      icon: ChartIcon,
      title: 'Объём',
      value: formatWork(mostVolume.total_work_all_time),
      subtitle: mostVolume.exercise_name,
      color: 'orange',
    });
  }

  // 5. Total exercises trained
  achievements.push({
    icon: TargetIcon,
    title: 'Арсенал',
    value: `${records.length} упр.`,
    subtitle: 'всего в базе',
    color: 'pink',
  });

  return achievements;
}

// Change indicator
function ChangeIndicator({ current, first, unit }: { current: number; first: number; unit: string }) {
  const diff = current - first;
  const isPositive = diff > 0;
  const isNegative = diff < 0;
  
  return (
    <div className={`text-sm font-medium ${
      isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-400'
    }`}>
      {isPositive ? '+' : ''}{diff.toFixed(1)} {unit}
    </div>
  );
}

export default function AnalyticsPage() {
  const [tab, setTab] = useState<TabMode>('training');
  const [selectedMeasurement, setSelectedMeasurement] = useState<MeasurementKey>('weight');

  const { data: calendarData } = useQuery({
    queryKey: ['calendar'],
    queryFn: async () => {
      const res = await analyticsApi.calendar({ months: 6 });
      return res.success ? res.data : null;
    },
  });

  const { data: records = [] } = useQuery({
    queryKey: ['records'],
    queryFn: async () => {
      const res = await analyticsApi.records();
      return res.success ? res.data : [];
    },
  });

  const { data: measurements = [], isLoading } = useQuery({
    queryKey: ['measurements'],
    queryFn: async () => {
      const res = await measurementsApi.list({ limit: 100 });
      return res.success ? res.data?.items.reverse() : [];
    },
  });

  if (isLoading && !calendarData && !records.length) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Prepare measurement chart data
  const measurementChartData = measurements
    .filter(m => m[selectedMeasurement] !== undefined && m[selectedMeasurement] !== null)
    .map(m => ({
      date: formatDate(m.date),
      value: m[selectedMeasurement] as number,
    }));

  const fieldConfig = MEASUREMENT_FIELDS.find(f => f.key === selectedMeasurement);

  // Before/After comparison
  const firstMeasurement = measurements[0];
  const lastMeasurement = measurements[measurements.length - 1];
  const hasMeasurements = measurements.length >= 2;

  return (
    <div className="page-container">
      <header className="page-header">
        {/* Tab navigation instead of title */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setTab('training')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              tab === 'training'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600'
            }`}
          >
            Тренировки
          </button>
          <button
            onClick={() => setTab('body')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              tab === 'body'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600'
            }`}
          >
            Тело
          </button>
        </div>
      </header>

      <div className="p-4 space-y-6">
        {tab === 'training' ? (
          <>
            {/* Streaks */}
            {calendarData && (
              <section>
                <h2 className="section-title">Активность</h2>
                <div className="card p-4">
                  {/* Streak circles */}
                  <div className="flex justify-center gap-8 mb-4">
                    <div className="w-20 flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center mb-2 shadow-lg shadow-orange-200">
                        <span className="text-2xl font-bold text-white">{calendarData.current_streak}</span>
                      </div>
                      <div className="flex items-center justify-center gap-1 text-xs text-gray-600">
                        <FlameIcon className="w-3.5 h-3.5 text-orange-500" />
                        <span>Серия</span>
                      </div>
                    </div>
                    <div className="w-20 flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center mb-2 shadow-lg shadow-amber-200">
                        <span className="text-2xl font-bold text-white">{calendarData.longest_streak}</span>
                      </div>
                      <div className="flex items-center justify-center gap-1 text-xs text-gray-600">
                        <TrophyIcon className="w-3.5 h-3.5 text-amber-500" />
                        <span>Рекорд</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-100">
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">{calendarData.this_week}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">Неделя</div>
                    </div>
                    <div className="text-center border-x border-gray-100">
                      <div className="text-lg font-bold text-gray-900">{calendarData.this_month}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">Месяц</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">{calendarData.total_workouts}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">Всего</div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Calendar */}
            {calendarData && (
              <section>
                <h2 className="section-title">Календарь активности</h2>
                <div className="card p-3 overflow-x-auto">
                  <CalendarHeatmap days={calendarData.days} />
                </div>
              </section>
            )}

            {/* Top Achievements */}
            <section>
              <h2 className="section-title">Достижения</h2>
              {records.length === 0 ? (
                <div className="card">
                  <div className="empty-state">
                    <p>Достижений пока нет</p>
                    <p className="text-sm mt-1">Завершите тренировки</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {getTopAchievements(records).slice(0, 4).map((achievement, i) => (
                    <AchievementCard key={i} {...achievement} />
                  ))}
                </div>
              )}
            </section>
          </>
        ) : (
          <>
            {/* Measurement Trends */}
            <section>
              <h2 className="section-title">Динамика измерений</h2>
              
              {measurements.length === 0 ? (
                <div className="card">
                  <div className="empty-state">
                    <p>Измерений пока нет</p>
                    <p className="text-sm mt-1">Добавьте измерения в разделе "Тело"</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Measurement selector */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {MEASUREMENT_FIELDS.map(field => {
                      const hasData = measurements.some(
                        m => m[field.key] !== undefined && m[field.key] !== null
                      );
                      if (!hasData) return null;
                      
                      return (
                        <button
                          key={field.key}
                          onClick={() => setSelectedMeasurement(field.key)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                            selectedMeasurement === field.key
                              ? 'text-white'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                          style={selectedMeasurement === field.key ? { backgroundColor: field.color } : {}}
                        >
                          {field.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Chart or disclaimer */}
                  {measurementChartData.length === 1 && (
                    <div className="card p-4">
                      <div className="text-center text-gray-500 py-4">
                        <p className="text-sm">Для графика нужно минимум 2 замера</p>
                        <p className="text-xs mt-1 text-gray-400">Добавьте ещё одно измерение</p>
                      </div>
                    </div>
                  )}
                  
                  {measurementChartData.length > 1 && (
                    <div className="card p-4">
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={measurementChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis
                              dataKey="date"
                              tick={{ fontSize: 10 }}
                              tickLine={false}
                              interval="preserveStartEnd"
                            />
                            <YAxis
                              tick={{ fontSize: 12 }}
                              tickLine={false}
                              domain={['auto', 'auto']}
                              tickFormatter={(v) => `${v}${fieldConfig?.unit || ''}`}
                            />
                            <Tooltip
                              formatter={(value: number) => [`${value} ${fieldConfig?.unit || ''}`, fieldConfig?.label || '']}
                              contentStyle={{
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke={fieldConfig?.color || '#3b82f6'}
                              strokeWidth={2}
                              dot={{ fill: fieldConfig?.color || '#3b82f6', r: 3 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>

            {/* Before/After Comparison */}
            {hasMeasurements && (
              <section>
                <h2 className="section-title">Было → Стало</h2>
                <div className="card divide-y divide-gray-100">
                  {MEASUREMENT_FIELDS.map(field => {
                    const firstVal = firstMeasurement[field.key];
                    const lastVal = lastMeasurement[field.key];
                    
                    if (firstVal === undefined || lastVal === undefined) return null;
                    
                    return (
                      <div key={field.key} className="p-3 flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">{field.label}</div>
                          <div className="text-sm text-gray-500">
                            {firstVal} → {lastVal} {field.unit}
                          </div>
                        </div>
                        <ChangeIndicator
                          current={lastVal as number}
                          first={firstVal as number}
                          unit={field.unit}
                        />
                      </div>
                    );
                  })}
                  
                  {firstMeasurement && lastMeasurement && (
                    <div className="p-3 text-xs text-gray-400 text-center">
                      {formatDate(firstMeasurement.date)} — {formatDate(lastMeasurement.date)}
                    </div>
                  )}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
